import React from "react";
import {
  RadioList,
  InputLabelStyleTypes,
  IRadioListProps,
  CheckboxGroup,
  Input,
  ICheckboxGroupConfig,
  ICheckboxGroupValue,
  QuestionTooltip,
  QuestionTooltipProps,
} from "component-library";
import { Trans } from "i18n";
import { ControlSection } from "../../../../../Common";
import {
  FacetCategoryViewModel,
  FacetViewModel,
  IDisabilitiesViewModel,
} from "../../../../models/views";

export interface IDisabilitiesContent {
  disabilitiesSectionName: string;
  disabilitiesSectionDescription: string;
  disabilitiesSectionDescriptionLink: string;
  disabilitiesSectionDescriptionLinkName: string;
  disabilitiesTogglerName: string;
  disabilitiesTogglerPositive: string;
  disabilitiesTogglerNegative: string;
  disabilitiesTooltip: QuestionTooltipProps;
  disabilitiesFurtherInfoField: string;
  disabilitiesShowOnProfileTogglerName: string;
  disabilitiesShowOnProfileTogglerNegative: string;
  disabilitiesShowOnProfileTogglerPositive: string;
  disabilitiesShowOnProfileTogglerPositiveDescription: string;
  disabilitiesShowOnProfileTogglerNegativeDescription: string;
}

interface IDisabilitiesProps {
  model: IDisabilitiesViewModel;
  facet: FacetCategoryViewModel;
  content: IDisabilitiesContent;
  handleChanges: (model: IDisabilitiesViewModel) => void;
}

const DISABILITIES_INFO_MAX_LENGTH = 75;

export default class Disabilities extends React.Component<IDisabilitiesProps> {
  public render() {
    const { model, content } = this.props;
    const options = this.getShowDisabilitiesRadioOptions();
    const description = this.renderSectionDescription();
    const annex = content.disabilitiesTooltip && this.getHasDisabilitiesAnnex();

    return (
      <ControlSection
        name={content.disabilitiesSectionName}
        description={description}
      >
        <div className="c-basic-info-form__disabilities">
          <RadioList
            name="hasDisability"
            value={model.hasDisability ?? false}
            options={options}
            labelStyleType={InputLabelStyleTypes.Secondary}
            isWidthLimited={true}
            label={content.disabilitiesTogglerName}
            valueChanged={this.handleHasDisabilitiesChange}
            vertical={true}
            annex={annex}
          />
        </div>
      </ControlSection>
    );
  }

  private renderSectionDescription = () => {
    const {
      disabilitiesSectionDescription,
      disabilitiesSectionDescriptionLink,
      disabilitiesSectionDescriptionLinkName,
    } = this.props.content;

    return (
      <div>
        {disabilitiesSectionDescription}&nbsp;
        <a
          href={disabilitiesSectionDescriptionLink}
          target="_blank"
          tabIndex={0}
          rel="noreferrer"
        >
          {disabilitiesSectionDescriptionLinkName}
        </a>
      </div>
    );
  };

  private renderDisabilitySpecificArea = () => {
    const { model, content, facet } = this.props;
    const options = this.mapFacetsToCheckboxOptions(facet.facets);

    return (
      <div className="c-basic-info-form__disabilities-area">
        <div className="c-basic-info-form__disabilities-section">
          <CheckboxGroup
            columns={{
              direction: "horizontal",
              count: { sm: 1, md: 2, lg: 2 },
            }}
            name="disabilities"
            config={{
              name: "disabilities",
              options: options,
            }}
            value={this.mapCheckboxGroupOptionsToValue(
              options,
              model.disabilities
            )}
            valueChanged={this.handleCheckboxGroupChange}
          />
          <div className="c-basic-info-form__disabilities-area-input">
            <Input
              id="disabilitiesFurtherInfo"
              name="disabilitiesFurtherInfo"
              type="text"
              label={content.disabilitiesFurtherInfoField}
              value={model.disabilityInfo ?? ""}
              maxLength={DISABILITIES_INFO_MAX_LENGTH}
              valueChanged={this.handleDisabilitiesInfoChange}
            />
          </div>
        </div>
        <div className="c-basic-info-form__disabilities-section">
          <RadioList<boolean>
            name="isVisibleDisability"
            value={model.isVisibleDisability ?? false}
            options={[
              {
                name: content.disabilitiesShowOnProfileTogglerNegative,
                value: false,
              },
              {
                name: content.disabilitiesShowOnProfileTogglerPositive,
                value: true,
              },
            ]}
            labelStyleType={InputLabelStyleTypes.Secondary}
            isWidthLimited={true}
            label={content.disabilitiesShowOnProfileTogglerName}
            valueChanged={this.handleShowDisabilitiesChange}
            vertical={true}
          />
          <div className="c-basic-info-form__disabilities-area-description">
            {model.isVisibleDisability ? (
              content.disabilitiesShowOnProfileTogglerPositiveDescription
            ) : (
              <Trans i18nKey="performer:heritage.ethnicity.visibility.no.description" />
            )}
          </div>
        </div>
      </div>
    );
  };

  private handleHasDisabilitiesChange = (value: boolean) => {
    this.props.handleChanges({
      ...this.props.model,
      hasDisability: value,
    });
  };

  private handleShowDisabilitiesChange = (value: boolean) => {
    this.props.handleChanges({
      ...this.props.model,
      isVisibleDisability: value,
    });
  };

  private handleCheckboxGroupChange = (value: ICheckboxGroupValue) => {
    this.props.handleChanges({
      ...this.props.model,
      disabilities: this.mapCheckboxValueToList(value),
    });
  };

  private handleDisabilitiesInfoChange = (value: string) => {
    this.props.handleChanges({
      ...this.props.model,
      disabilityInfo: value,
    });
  };

  private getShowDisabilitiesRadioOptions =
    (): IRadioListProps<boolean>["options"] => {
      const { content } = this.props;

      return [
        {
          name: content.disabilitiesTogglerNegative,
          value: false,
        },
        {
          name: content.disabilitiesTogglerPositive,
          value: true,
          renderExpandingArea: this.renderDisabilitySpecificArea,
        },
      ];
    };

  private mapFacetsToCheckboxOptions = (
    facets: FacetViewModel[]
  ): ICheckboxGroupConfig["options"] => {
    return facets.reduce(
      (result, item) => {
        result.push({ label: item.name, value: String(item.id) });

        return result;
      },
      [] as ICheckboxGroupConfig["options"]
    );
  };

  private mapCheckboxGroupOptionsToValue(
    checkboxOptions: ICheckboxGroupConfig["options"] = [],
    values: FacetViewModel[] = []
  ): ICheckboxGroupValue {
    return checkboxOptions.reduce((result, option) => {
      if ("value" in option) {
        result[option.value] = values.some(
          (value) => value.id === Number(option.value)
        );
      }

      return result;
    }, {} as ICheckboxGroupValue);
  }

  private mapCheckboxValueToList(
    values: ICheckboxGroupValue
  ): FacetViewModel[] {
    return Object.keys(values).reduce((result, key) => {
      if (values[key]) {
        result.push({ id: Number(key), name: "", code: "" });
      }

      return result;
    }, [] as FacetViewModel[]);
  }

  private getHasDisabilitiesAnnex = () => {
    return <QuestionTooltip {...this.props.content.disabilitiesTooltip} />;
  };
}
